import { useRef, useState } from "react";
import { Download, Play } from "lucide-react";
import chaptersIt from "@/data/tourChapters.json";
import chaptersEn from "@/data/tourChapters_en.json";
import { PageHeader } from "@/components/Badges";
import { Button } from "@/components/ui/button";
import { t, getLang } from "@/lib/i18n";

const TITLES = ["Introduzione", "L'albero a 8 rami", "La scheda nodo", "Proposte di crescita dell'albero", "Il Cockpit direzionale", "Il team: un ruolo per ramo", "Team AI sul corpus", "Risposta rapida a regole", "La riunione di direzione", "Caricamento dispense", "Food cost preventivo e consuntivo", "Inventario rapido da smartphone", "Menu engineering", "Menù stampabile", "Vendite da cassa", "Storico vendite", "Carta dei vini", "Sommelier AI", "Pianificatore eventi", "Ricerca territoriale", "Corpus documentale", "Analisi gap", "Report e conclusione"];
const fmt = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

export default function GuidePage() {
  const ref = useRef(null);
  const chapters = getLang() === "en" ? chaptersEn : chaptersIt;
  const src = getLang() === "en" ? "/media/tour_en.mp4" : "/media/tour.mp4";
  const [time, setTime] = useState(0);
  const starts = chapters.reduce((acc, c, i) => [...acc, (acc[i - 1] ?? 0) + (i ? chapters[i - 1].dur : 0)], []);
  const current = starts.findLastIndex((s) => time >= s);
  const seek = (i) => { ref.current.currentTime = starts[i] + 0.1; ref.current.play().catch(() => {}); };

  return (
    <div data-testid="guide-page">
      <PageHeader eyebrow={t("Video illustrativo · 7 min 15 s · 23 capitoli · voce narrante in italiano")} title={t("Guida al funzionamento")}
        subtitle={t("Un tour completo dell'assistente di direzione: albero e proposte AI, Cockpit, Team AI sul corpus, caricamento dispense, calcolatori, inventario rapido, menù stampabile, vendite da cassa con storico, carta dei vini con Sommelier AI, eventi, ricerca territoriale e report mensile.")}>
        <Button asChild variant="outline" data-testid="guide-download-btn"><a href={src} download="FB-Wine-Management-guide.mp4"><Download size={14} className="mr-1.5" /> {t("Scarica MP4")}</a></Button>
      </PageHeader>
      <div className="px-5 lg:px-8 py-6 grid xl:grid-cols-[1fr_360px] gap-6">
        <div className="surface overflow-hidden self-start">
          <video data-testid="guide-video" ref={ref} controls playsInline preload="metadata" className="w-full aspect-video bg-black" onTimeUpdate={(e) => setTime(e.target.currentTime)}>
            <source src={src} type="video/mp4" />
            {t("Il tuo browser non supporta la riproduzione video:")} <a href={src} download>{t("scarica il file MP4")}</a>.
          </video>
          {current >= 0 && <div className="p-4 border-t border-border"><div className="eyebrow mb-1">Capitolo {current + 1} · {t(TITLES[current])}</div><p className="text-sm text-foreground/85">{chapters[current].text}</p></div>}
        </div>
        <aside className="surface p-3 space-y-1 self-start">
          <div className="eyebrow px-2 py-1">{t("Capitoli")}</div>
          {chapters.map((c, i) => (
            <button key={i} data-testid={`guide-chapter-${i}`} onClick={() => seek(i)} className={`w-full text-left flex items-center gap-3 px-2 py-2 rounded-md transition-colors ${current === i ? "bg-primary/15 text-primary" : "hover:bg-accent"}`}>
              <span className="num text-xs text-muted-foreground w-9">{fmt(starts[i])}</span>
              <span className="text-sm flex-1">{t(TITLES[i])}</span>
              {current === i && <Play size={12} />}
            </button>
          ))}
        </aside>
      </div>
    </div>
  );
}
